import { Column, DataTable, Row, tableScroll } from '@ydistri/ds';
import { evaluationsRowClassName, getBaseExecutionColumns } from '../../evaluationsLib';
import React, { useCallback, useMemo } from 'react';
import { useGetStoreEvaluationRatesQuery } from '../../apiCalculationEvaluations';
import {
  EntityColumns,
  EntityColumnsConfig,
  useStoreTableColumnsProvider,
} from '../../../../../hooks/useEntityTableColumnsProvider';
import {
  CalculationStatisticClusterType,
  SourceStoreEvaluationRatesResponse,
} from '@ydistri/api-sdk';
import { ExpandableConfig } from 'antd/es/table/interface';
import TargetStoreDetailTable from '../../TargetStoreDetailTable';
import { useCurrency } from '../../../../../hooks/useCurrency';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../store';
import { useTemplateOrCalculation } from '../../../../../hooks/useTemplateOrCalculation';
import LoadingIndicator from '../../../../../components/global/LoadingIndicator/LoadingIndicator';
import ScrolledTableWrapper from '../../../../../components/global/ScrolledTableWrapper/ScrolledTableWrapper';
import StoreEvaluationsDownloadButton from '../StoreEvaluationsDownloadButton/StoreEvaluationsDownloadButton';
import { useApplicationConfiguration } from '../../../../../hooks/useApplicationConfiguration';
import { computeRemSize } from '@ydistri/theme';

interface StoreEvaluationsTableProps {
  long?: boolean;
}

const StoreEvaluationsTable: React.FC<StoreEvaluationsTableProps> = ({ long = false }) => {
  const templateOrCalculation = useTemplateOrCalculation();
  const currencyId = useCurrency();
  const clusterType = useSelector((state: ReduxState) => state.evaluations.clusterType);
  const columnsProvider = useStoreTableColumnsProvider();
  const { isEvaluationDeliveryEnabled } = useApplicationConfiguration();

  const { data, isFetching: isFetchingStatistics } = useGetStoreEvaluationRatesQuery({
    calculationId: templateOrCalculation.id,
    clusterType,
  });

  const columns = useMemo(() => {
    const columnsConfig: EntityColumnsConfig<SourceStoreEvaluationRatesResponse> = {
      [EntityColumns.ID]: {
        width: computeRemSize(150),
        title: 'Sending Store Id',
      },
      [EntityColumns.CODE]: {
        width: computeRemSize(100),
        title: 'Sending Store Code',
      },
      [EntityColumns.NAME]: {
        title: 'Sending Store Name',
      },
    };
    const storeColumns = columnsProvider<SourceStoreEvaluationRatesResponse>(columnsConfig);

    return getBaseExecutionColumns(currencyId, storeColumns, {
      includeDepartments: clusterType === CalculationStatisticClusterType.StoreDepartment,
      includeRoutes: true,
      includeDeliveredValue: isEvaluationDeliveryEnabled,
    });
  }, [columnsProvider, currencyId, clusterType, isEvaluationDeliveryEnabled]);

  const expandableConfig: ExpandableConfig<SourceStoreEvaluationRatesResponse> = useMemo(
    () => ({
      expandedRowRender: record => {
        if (!('store' in record)) return null;
        return (
          <TargetStoreDetailTable
            sourceStoreId={record.store.id}
            departmentId={record.department?.id}
          />
        );
      },
    }),
    [],
  );

  const rowKeyProvider = useCallback((record: SourceStoreEvaluationRatesResponse) => {
    const keyParts: string[] = [record.store.id.toString()];
    keyParts.push(record.department ? record.department.id.toString() : 'no-department');
    return keyParts.join('-');
  }, []);

  return (
    <Column $padding={1} $gap="4pt" $flexGrow={1}>
      {isFetchingStatistics && !data && <LoadingIndicator />}
      {data && (
        <>
          <Row $justifyContent="flex-end">
            <StoreEvaluationsDownloadButton
              calculationId={templateOrCalculation.id}
              clusterType={clusterType}
            />
          </Row>
          <ScrolledTableWrapper offset={long ? 10 : 40} minHeight={440}>
            <DataTable<SourceStoreEvaluationRatesResponse>
              id="store-evaluations"
              scroll={tableScroll}
              rowKey={rowKeyProvider}
              dataSource={data}
              columns={columns}
              loading={isFetchingStatistics}
              showHeader={true}
              rowClassName={evaluationsRowClassName}
              expandable={expandableConfig}
            />
          </ScrolledTableWrapper>
        </>
      )}
    </Column>
  );
};

export default StoreEvaluationsTable;
